import { useEffect } from "react";
import {
  stripMathJaxInlineSizes,
  typesetMathJaxElements,
} from "./useMathjax";

/**
 * Publish-paper-only MathJax loader.
 *
 * MathJax v4 is intentionally isolated to GeneratePaper because v4 line
 * breaking changes pagination/layout in the shared Solution/editor consumers.
 * Publish outputs are opened in a separate tab, so this document loads only
 * one MathJax runtime.
 */
const loadPrintMathJaxScript = () => {
  if (document.getElementById("mathjax-script")) {
    return;
  }

  window.MathJax = {
    tex: {
      inlineMath: [["$", "$"], ["\\(", "\\)"]],
      displayMath: [["$$", "$$"], ["\\[", "\\]"]],
      processEscapes: true,
    },
    output: {
      // Keep the familiar MathJax TeX appearance and normal scale.
      font: "mathjax-tex",
      scale: 1,

      // Never shrink a long expression. MathJax moves the overflowing part
      // to a following line at a valid mathematical break point.
      displayOverflow: "linebreak",
      linebreaks: {
        inline: true,
        width: "100%",
        lineleading: 0.2,
      },
    },
    options: {
      skipHtmlTags: ["script", "noscript", "style", "textarea", "pre"],
    },
    startup: {
      ready() {
        window.MathJax.startup.defaultReady();
        window.MathJax.startup.promise.then(stripMathJaxInlineSizes);
      },
    },
  };

  const script = document.createElement("script");
  script.src =
    "https://cdn.jsdelivr.net/npm/mathjax@4.1.3/tex-mml-chtml-nofont.js";
  script.async = true;
  script.id = "mathjax-script";
  script.onerror = () => {
    console.warn("Print MathJax failed to load from CDN.");
  };
  document.body.appendChild(script);
};

const usePrintMathjax = (dependency, dependency1) => {
  useEffect(() => {
    loadPrintMathJaxScript();

    const timer = setTimeout(() => {
      typesetMathJaxElements(document.body);
    }, 0);

    return () => clearTimeout(timer);
  }, [dependency, dependency1]);
};

export default usePrintMathjax;
